
export interface IMeta {
  key: string;
  value: string;
}

export interface IWithMeta {
  meta?: IMeta[] | null;
}

export interface IWithQuestionKey {
  questionKey?: string | null;
}

export interface IAnswer extends IWithMeta {
  wording?: string | null;
  key?: string | null;
}

export interface IResponse extends IWithQuestionKey {
  answers?: IAnswer[]
  freeText?: string;
}

export interface UnwrapResponseArgs<R extends IResponse, K extends string> {
  responses: R[];
  questionKey: string;
  findAnswer: <T extends IAnswer>(answer?: T) => boolean,
  metaKeys: K[];
}

export interface UnwrapQuestion<K extends string> {
  questionKey: string;
  findAnswer: <T extends IAnswer>(answer?: T) => boolean,
  metaKeys: readonly K[];
}
export interface UnwrapAnswer<K extends string> {
  answer: IAnswer;
  metaKeys: readonly K[]
}


export class QueryResponses<T extends IResponse> {
  private lookup: Record<string, T> = {};
  constructor(responses: T[]) {
    for (const r of responses) {
      if (r.questionKey) {
        this.lookup[r.questionKey] = r;
      }
    }
  }
  has(questionKey: string): boolean {
    return this.lookup[questionKey] !== undefined;
  }
  get(questionKey: string): T | undefined {
    return this.lookup[questionKey];
  }
  getFirstAnswer(questionKey: string) {
    return this.lookup[questionKey]?.answers?.[0];
  }
  getText(questionKey: string) {
    return this.lookup[questionKey]?.freeText;
  }
  getMultiWording(questionKey: string) {
    return this.lookup[questionKey]?.answers?.map(a => a.wording);
  }
  unwrap<K extends string>(args: UnwrapQuestion<K> | UnwrapAnswer<K>) {
    if ('questionKey' in args) {
      const response = this.get(args.questionKey);
      const answer = response?.answers?.find(args.findAnswer);
      return this.unwrapMeta(args.metaKeys, answer);
    }
    return this.unwrapMeta(args.metaKeys, args.answer);
  }
  getMetaValue = (key: string, a?: IWithMeta) =>
    a?.meta?.find(m => m.key === key)?.value;
  unwrapMeta = <T extends string>(keys: readonly T[], a?: IWithMeta): Record<T, string | undefined> => {
    return keys.reduce((meta, key) => {
      const value = this.getMetaValue(key, a);
      meta[key] = value;
      return meta;
    },{} as Record<T, string | undefined>);
  };
}
